import Swiper from "./swiper-bundle.esm.browser.min.js";

const MOBILE_BREAKPOINT = 769;
const DESKTOP_SLIDER_THRESHOLD = 4;

class BlogArticleSlider extends HTMLElement {
  constructor() {
    super();
    this._swiper = null;
    this._resizeTimer = null;
    this._initialized = false;
    this._handleResize = this._handleResize.bind(this);
    this._handleSectionLoad = this._handleSectionLoad.bind(this);
    this._handleSectionUnload = this._handleSectionUnload.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    document.addEventListener("shopify:section:load", this._handleSectionLoad);
    document.addEventListener("shopify:section:unload", this._handleSectionUnload);
    window.addEventListener("resize", this._handleResize, { passive: true });
    this._updateLayout();
  }

  disconnectedCallback() {
    this._initialized = false;
    this._destroySwiper();
    window.removeEventListener("resize", this._handleResize);
    document.removeEventListener("shopify:section:load", this._handleSectionLoad);
    document.removeEventListener("shopify:section:unload", this._handleSectionUnload);
    if (this._resizeTimer) {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = null;
    }
  }

  _isOurSection(event) {
    const section = this.closest(".shopify-section");
    return section && (event.target === section || section.contains(event.target));
  }

  _handleSectionLoad(event) {
    if (!this._isOurSection(event)) return;
    this._destroySwiper();
    this._updateLayout();
  }

  _handleSectionUnload(event) {
    if (!this._isOurSection(event)) return;
    this._destroySwiper();
  }

  _readConfig() {
    const articleCount = parseInt(this.dataset.articleCount || "0", 10);
    return {
      articleCount,
      sectionId: this.dataset.sectionId || "",
      spaceBetween: parseInt(this.dataset.spaceBetween || "20", 10),
      mobileSlidesPerView: parseFloat(this.dataset.slidesPerViewMobile || "1.2"),
      desktopSlidesPerView: parseFloat(this.dataset.slidesPerViewDesktop || "4"),
      showArrows: this.dataset.showArrows === "true",
      showPagination: this.dataset.showPagination === "true",
      enableLoop: this.dataset.enableLoop === "true",
    };
  }

  _isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  _shouldUseSwiper(config) {
    if (config.articleCount <= 0) return false;
    return this._isMobile() || config.articleCount > DESKTOP_SLIDER_THRESHOLD;
  }

  _cleanupSwiperClasses() {
    const swiperEl = this.querySelector("[data-swiper]");
    const container = this.querySelector("[data-swiper-container]");
    const slides = this.querySelectorAll("[data-swiper-slide]");

    if (swiperEl) {
      swiperEl.classList.remove("swiper", "blog-article-slider__swiper", "swiper-initialized");
    }
    if (container) {
      container.classList.remove("swiper-wrapper");
    }
    slides.forEach((slide) => slide.classList.remove("swiper-slide"));
  }

  _applyGridMode(config) {
    this.classList.add("blog-article-slider--grid");
    this.classList.remove("blog-article-slider--swiper");
    this.style.setProperty(
      "--bas-grid-columns",
      String(Math.min(config.articleCount, DESKTOP_SLIDER_THRESHOLD)),
    );
    this._cleanupSwiperClasses();
  }

  _prepareSwiperStructure() {
    const swiperEl = this.querySelector("[data-swiper]");
    const container = this.querySelector("[data-swiper-container]");
    const slideEls = this.querySelectorAll("[data-swiper-slide]");

    if (!swiperEl || !container || slideEls.length === 0) return null;

    swiperEl.classList.add("swiper", "blog-article-slider__swiper");
    container.classList.add("swiper-wrapper");
    slideEls.forEach((slide) => slide.classList.add("swiper-slide"));

    return { swiperEl, slideEls };
  }

  _syncSlideAccessibility(activeIndex) {
    const slides = this.querySelectorAll("[data-swiper-slide]");
    slides.forEach((slide, index) => {
      const isActive = index === activeIndex;
      slide.setAttribute("aria-hidden", isActive ? "false" : "true");
    });
  }

  _initSwiper(config) {
    const structure = this._prepareSwiperStructure();
    if (!structure) return;

    const { swiperEl, slideEls } = structure;
    const isMobile = this._isMobile();
    const slidesPerView = isMobile
      ? config.mobileSlidesPerView
      : config.desktopSlidesPerView;
    const canNavigate = slideEls.length > 1;
    const canLoop =
      config.enableLoop && canNavigate && slideEls.length > slidesPerView;

    this.classList.add("blog-article-slider--swiper");
    this.classList.remove("blog-article-slider--grid");

    const swiperConfig = {
      slidesPerView,
      spaceBetween: config.spaceBetween,
      watchOverflow: true,
      grabCursor: canNavigate,
      speed: 300,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      a11y: {
        enabled: true,
        prevSlideMessage: "Previous article",
        nextSlideMessage: "Next article",
      },
    };

    if (canLoop) {
      swiperConfig.loop = true;
    }

    if (config.showArrows && canNavigate) {
      const nextEl = this.querySelector(
        `.blog-article-slider__nav-next--${config.sectionId}`,
      );
      const prevEl = this.querySelector(
        `.blog-article-slider__nav-prev--${config.sectionId}`,
      );
      if (nextEl && prevEl) {
        swiperConfig.navigation = { nextEl, prevEl };
      }
    }

    if (config.showPagination && canNavigate) {
      const paginationEl = this.querySelector(
        `.blog-article-slider__pagination--${config.sectionId}`,
      );
      if (paginationEl) {
        swiperConfig.pagination = {
          el: paginationEl,
          clickable: true,
          renderBullet: (index, className) => {
            const num = index + 1;
            const total = config.articleCount;
            return `<button type="button" class="${className}" aria-label="Go to article ${num} of ${total}"></button>`;
          },
        };
      }
    }

    const existingOn = swiperConfig.on || {};
    swiperConfig.on = {
      ...existingOn,
      afterInit: (swiper) => {
        existingOn.afterInit?.(swiper);
        this._syncSlideAccessibility(swiper.realIndex ?? swiper.activeIndex);
      },
      slideChangeTransitionEnd: (swiper) => {
        existingOn.slideChangeTransitionEnd?.(swiper);
        this._syncSlideAccessibility(swiper.realIndex ?? swiper.activeIndex);
      },
      resize: (swiper) => {
        existingOn.resize?.(swiper);
        swiper.update();
      },
    };

    this._swiper = new Swiper(swiperEl, swiperConfig);
    requestAnimationFrame(() => this._swiper?.update());
  }

  _destroySwiper() {
    if (this._swiper && !this._swiper.destroyed) {
      this._swiper.destroy(true, true);
    }
    this._swiper = null;
    this._cleanupSwiperClasses();
  }

  _updateLayout() {
    const config = this._readConfig();
    const shouldUseSwiper = this._shouldUseSwiper(config);
    const isSwiperActive = Boolean(this._swiper);

    if (!shouldUseSwiper) {
      this._destroySwiper();
      this._applyGridMode(config);
      return;
    }

    if (isSwiperActive) {
      this._destroySwiper();
    }

    this._initSwiper(config);
  }

  _handleResize() {
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => {
      const config = this._readConfig();
      const shouldUseSwiper = this._shouldUseSwiper(config);
      const isSwiperActive = Boolean(this._swiper);

      if (shouldUseSwiper !== isSwiperActive) {
        this._updateLayout();
        return;
      }

      if (this._swiper && !this._swiper.destroyed) {
        const isMobile = this._isMobile();
        const slidesPerView = isMobile
          ? config.mobileSlidesPerView
          : config.desktopSlidesPerView;
        this._swiper.params.slidesPerView = slidesPerView;
        this._swiper.update();
      }
    }, 150);
  }
}

if (!customElements.get("blog-article-slider")) {
  customElements.define("blog-article-slider", BlogArticleSlider);
}
